import React, { useMemo } from "react";
import { FormField } from "../../../../shared/components/forms/FormField";
import { Card } from "../../../../shared/components/ui/Card";
import { InformationalNote } from "../../../../shared/components/ui/InformationalNote";
import type { UseFormRegister, FieldErrors } from "react-hook-form";
import type { MaintenanceType } from "../../../../api/client/types/catalog.types";
import type { MaintenanceNewFormData } from "./MaintenanceNewFormFields.types";

interface MaintenanceNewFormFieldsProps {
  register: UseFormRegister<MaintenanceNewFormData>;
  errors: FieldErrors<MaintenanceNewFormData>;
  maintenanceTypes: MaintenanceType[];
  showCard?: boolean; // Control whether to wrap content in a Card component
  hideTypeSelector?: boolean; // Control whether to hide the maintenance type selector (for when type is pre-selected at report type level)
}

/**
 * MaintenanceNewFormFields
 * Renders only the maintenance-specific fields.
 * Designed to be used within a parent form that handles common fields.
 * 
 * @param showCard - If true, wraps content in a Card with title. Default: true
 * @param hideTypeSelector - If true, hides the maintenance type selector. Default: false
 */
export const MaintenanceNewFormFields: React.FC<MaintenanceNewFormFieldsProps> = ({
  register,
  errors,
  maintenanceTypes,
  showCard = true,
  hideTypeSelector = false,
}) => {
  const maintenanceTypeOptions = useMemo(
    () =>
      maintenanceTypes
        // Filter out "Elementos de Emergencia" as it appears at the report type level
        .filter((type) => type.name !== "Elementos de Emergencia")
        .map((maintenanceType) => ({
          label: maintenanceType.name,
          value: maintenanceType.id.toString(),
        })),
    [maintenanceTypes]
  );

  const content = (
    <>
      {/* Maintenance Type - Only show if not hidden */}
      {!hideTypeSelector && (
        <FormField
          type="select"
          label="Tipo de Mantenimiento"
          required
          selectProps={{
            ...register("maintenance_type_id"),
            options: [
              { value: "", label: "Seleccione un tipo de mantenimiento" },
              ...maintenanceTypeOptions,
            ],
            className: "text-sm",
          }}
          error={errors.maintenance_type_id?.message}
        />
      )}

      {/* Informational Note */}
      <InformationalNote className={hideTypeSelector ? "" : "mt-5"}>
        Al finalizar cualquiera de estas notificaciones, debe enviar una Foto o
        Video a la Central de Comunicaciones de la Condición presentada
      </InformationalNote>
    </>
  );

  if (!showCard) {
    return content;
  }

  return (
    <Card variant="formSection">
      <h2 className="text-lg font-medium text-gray-800 mb-4">
        Información de Mantenimiento
      </h2>
      {content}
    </Card>
  );
};

